import React, { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useGetSupplier } from '../hooks/useGetSupplier';
import {
  Container,
  Row,
  Col,
  Tab,
  Tabs,
  Button,
  ListGroup,
  ToastContainer,
  Toast,
  Table,
} from 'react-bootstrap';
import EditAddressModal from '../components/EditAddressModal';
import type { Address, Contact, UserOrganisation } from '../types/types';
import { useUpdateSupplier } from '../hooks/useUpdateSupplier';
import { InlineEditText } from '../components/common/InlineTextEdit';
import { useIndustries } from '../hooks/useGetIndustries';
import { countryList } from '../const/const';
import EditSupplierCountry from '../components/EditSupplierCountry';
import EditContactModal from '../components/EditContactModal';
import { SupplierDocuments } from '../components/SupplierDocuments';
import EditSupplierName from '../components/EditSupplierName';
import { LoaderContainer } from '../components/common/LoaderContainer';
import { SentAssessmentsList } from '../components/questionnaireBuilder/SentAssessmentsList';
import { useSelector } from 'react-redux';
import { ConfirmDeleteContactModal } from '../components/supplier/ConfirmDeleteContactModal';
import { gToastPosition, gToastSentiment } from '../util/constst';

const SupplierDetails: React.FC = () => {
  const { supplierId } = useParams<{ supplierId: string }>();
  const location = useLocation();
  const from = (location.state as { from?: Location })?.from;
  const {
    supplier,
    industries: supplierIndustries,
    contacts,
    loading,
    error,
    mutate,
    mutateContacts,
    fetchContacts,
  } = useGetSupplier(supplierId ?? '');
  const {
    updateSupplier,
    updateSupplierAddress,
    saveContact,
    deleteContact,
    loading: updating,
  } = useUpdateSupplier();
  const { industries } = useIndustries(); // TODO: dynamic country code

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [showEditCountryModal, setShowEditCountryModal] =
    useState<boolean>(false);
  const [editContact, setEditContact] = useState<Contact>(); // contact ID being edited
  const [deletedContact, setDeletedContact] = useState<Contact>(); // contact ID being edited
  const [showConfirmDeleteContactModal, setShowConfirmDeleteContactModal] =
    useState(false);

  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  if (!supplierId) return <p>Supplier ID is missing</p>;
  if (loading) return <LoaderContainer />;
  if (error) return <p>Error loading supplier: {error.message}</p>;
  if (!supplier) return <p>Supplier not found</p>;

  const currentAddress = supplier.primary_address;
  const addressId = currentAddress?.id;

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const handleSaveAddress = async (newAddress: Address) => {
    // 🔑 CRITICAL: We now need the Address ID, not the Supplier ID
    if (!addressId || !newAddress) {
      showToastMessage('Error: Cannot update address without an Address ID.');
      return;
    }

    // Assume updateSupplierAddress now takes the Address ID as the first argument
    const success = await updateSupplierAddress(addressId, {
      street: newAddress.street,
      city: newAddress.city,
      state: newAddress.state ?? '',
      postal_code: newAddress.postal_code,
      country: newAddress.country,
    });

    if (success) {
      showToastMessage('Address updated successfully');
      setShowAddressModal(false);

      // 🔑 CRITICAL: Update the local state (mutate) by merging the new address
      // into the nested primary_address object.
      mutate({
        primary_address: {
          ...currentAddress, // Keep the ID and other original fields
          street: newAddress.street,
          city: newAddress.city,
          state: newAddress.state ?? '',
          postal_code: newAddress.postal_code,
          country: newAddress.country,
        },
      });
    } else {
      showToastMessage('Failed to update address');
    }
  };

  const handleUpdateContact = async (newContact: Contact) => {
    if (!supplierId || !newContact) return;

    const success = await saveContact(supplierId, newContact.id, {
      name: newContact.name,
      email: newContact.email,
      phone: newContact.phone ?? '',
    });

    if (success) {
      showToastMessage('Contact updated successfully');
      setEditContact(undefined);

      mutateContacts(
        contacts.map((c) =>
          c.id === newContact.id ? { ...c, ...newContact } : c,
        ),
      );
      fetchContacts();
    } else {
      showToastMessage('Failed to update contact');
    }
  };

  const updateField = async (field: string, value: string) => {
    if (!supplierId) return;

    const success = await updateSupplier(supplierId, { [field]: value });
    if (success) {
      showToastMessage(`${field.replace('_', ' ')} updated successfully`);
      mutate({ [field]: value });
    } else {
      showToastMessage(`Failed to update ${field.replace('_', ' ')}`);
    }
  };

  return (
    <Container fluid className="mt-4">
      <Row className="mb-3">
        <Col>
          <Link to={from ? `${from.pathname}${from.search}` : '/suppliers'}>
            <Button variant="secondary">&larr; Back to Suppliers</Button>
          </Link>
        </Col>
      </Row>

      <Row>
        <Col className="d-flex align-items-center">
          <h2>{supplier.company_name}</h2>{' '}
          <i
            role="button"
            onClick={() => setShowEditNameModal(true)}
            className="bi bi-pencil-square ms-3"
          ></i>
        </Col>
      </Row>

      <Tabs defaultActiveKey="supplier" id="supplier-tabs" className="mt-3">
        <Tab eventKey="supplier" title="Supplier">
          <Row className="mt-3">
            <Col md={6}>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <strong>Trading Name:</strong>{' '}
                  <InlineEditText
                    value={supplier.trading_name}
                    onSave={(val) => updateField('trading_name', val)}
                  />
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Registration Number:</strong>{' '}
                  <InlineEditText
                    value={supplier.registration_number}
                    onSave={(val) => updateField('registration_number', val)}
                  />
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Country:</strong>{' '}
                  {countryList.find((c) => c.isoCode === supplier.country)
                    ?.countryName || supplier.country}{' '}
                  <i
                    role="button"
                    onClick={() => setShowEditCountryModal(true)}
                    className="bi bi-pencil-square"
                  ></i>
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Tax ID:</strong>{' '}
                  <InlineEditText
                    value={supplier.tax_id}
                    onSave={(val) => updateField('tax_id', val)}
                  />
                </ListGroup.Item>

                <ListGroup.Item>
                  <strong>Email:</strong>{' '}
                  <InlineEditText
                    value={supplier.email ?? ''}
                    onSave={(val) => updateField('email', val)}
                  />
                  <a href={`mailto:${supplier.email}`}>
                    <i className="bi bi-envelope-arrow-up"></i>
                  </a>
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Phone:</strong>{' '}
                  <InlineEditText
                    value={supplier.phone ?? ''}
                    onSave={(val) => updateField('phone', val)}
                  />
                </ListGroup.Item>
              </ListGroup>

              <hr />
              <div className="d-flex justify-content-between align-items-center">
                <h5>Contacts</h5>
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-2"
                  onClick={() =>
                    setEditContact({
                      id: uuidv4(),
                      name: '',
                      email: '',
                      phone: '',
                    })
                  }
                >
                  + Add contact
                </Button>
              </div>
              <div className="align-items-center mb-3">
                <Table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact) => (
                      <tr key={contact.id}>
                        <td>{contact.name || supplier.company_name}</td>
                        <td>{contact.phone}</td>
                        <td>
                          <a href={`mailto:${contact.email}`}>
                            {contact.email}
                          </a>
                        </td>
                        <td>
                          <i
                            role="button"
                            onClick={() => setEditContact(contact)}
                            className="bi bi-pencil-square"
                          ></i>
                          <i
                            className="bi bi-trash ms-3"
                            role="button"
                            onClick={() => {
                              // console.log('Delete contact not implemented yet');
                              // deleteContact(supplierId, contact.id).then(
                              //   (success) => {
                              //     if (success) {
                              //       showToastMessage(
                              //         'Contact deleted successfully'
                              //       );
                              //       mutateContacts(
                              //         contacts.filter(
                              //           (c) => c.id !== contact.id
                              //         )
                              //       );
                              //     } else {
                              //       showToastMessage(
                              //         'Failed to delete contact'
                              //       );
                              //     }
                              //   }
                              // );
                              setShowConfirmDeleteContactModal(true);
                              setDeletedContact(contact);
                            }}
                          ></i>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Col>
            <Col md={6}>
              <h5>Address</h5>
              <div className="d-flex align-items-center mb-3">
                <i className="bi bi-geo-alt"></i>
                <p className="ms-3 mb-0 me-3">
                  {currentAddress?.street}, {currentAddress?.city},{' '}
                  {currentAddress?.state}, {currentAddress?.postal_code},{' '}
                  {currentAddress?.country}
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowAddressModal(true)}
                >
                  Edit
                </Button>
              </div>
              <h5>Industry</h5>
              {supplierIndustries.length > 0 ? (
                <ListGroup variant="flush">
                  {supplierIndustries.map((id) => {
                    const industry = industries.find((ind) => ind.id === id);
                    return (
                      <ListGroup.Item key={id}>
                        {industry ? industry.label : 'Unknown Industry'}
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              ) : (
                <p>No industries assigned.</p>
              )}
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="bank" title="Bank Details">
          <Row className="mt-3">
            <Col md={6}>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <strong>Bank Name:</strong>{' '}
                  <InlineEditText
                    value={supplier.bank_name ?? ''}
                    onSave={(val) => updateField('bank_name', val)}
                  />
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Branch:</strong>{' '}
                  <InlineEditText
                    value={supplier.bank_branch ?? ''}
                    onSave={(val) => updateField('bank_branch', val)}
                  />
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Account Number:</strong>{' '}
                  <InlineEditText
                    value={supplier.bank_account_number ?? ''}
                    onSave={(val) => updateField('bank_account_number', val)}
                  />
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>IBAN:</strong>{' '}
                  <InlineEditText
                    value={supplier.iban ?? ''}
                    onSave={(val) => updateField('iban', val)}
                  />
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>SWIFT Code:</strong>{' '}
                  <InlineEditText
                    value={supplier.swift_code ?? ''}
                    onSave={(val) => updateField('swift_code', val)}
                  />
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Payment Terms:</strong>{' '}
                  <InlineEditText
                    value={supplier.payment_terms ?? ''}
                    onSave={(val) => updateField('payment_terms', val)}
                  />
                </ListGroup.Item>
              </ListGroup>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="assessments" title="Assessments" className="pt-3">
          <SentAssessmentsList
            buyerOrgId={selectedOrganisation?.organisation?.id || ''}
            supplierId={supplierId}
          />
        </Tab>

        <Tab eventKey="compliance" title="Compliance">
          <p className="mt-3">Compliance information will be added here.</p>
        </Tab>
        <Tab eventKey="documents" title="Documents">
          <SupplierDocuments supplierId={supplierId} />
        </Tab>
      </Tabs>

      {/* Edit Address Modal */}
      <EditAddressModal
        show={showAddressModal}
        onHide={() => setShowAddressModal(false)}
        loading={updating}
        address={{
          // 🔑 CHANGE 4: Pass the current address from the nested object
          street: currentAddress?.street ?? '',
          city: currentAddress?.city ?? '',
          state: currentAddress?.state ?? '',
          postal_code: currentAddress?.postal_code ?? '',
          country: currentAddress?.country ?? '',
        }}
        onSave={handleSaveAddress}
      />

      <EditContactModal
        show={editContact != undefined}
        onHide={() => setEditContact(undefined)}
        loading={updating}
        contact={editContact}
        onSave={handleUpdateContact}
      />

      <ConfirmDeleteContactModal
        show={showConfirmDeleteContactModal}
        onHide={() => setShowConfirmDeleteContactModal(false)}
        deletedContact={deletedContact}
        onConfirm={() => {
          if (deletedContact) {
            deleteContact(supplierId, deletedContact.id).then((success) => {
              if (success) {
                showToastMessage('Contact deleted successfully');
                mutateContacts(
                  contacts.filter((c) => c.id !== deletedContact.id),
                );
              } else {
                showToastMessage('Failed to delete contact');
              }
              setShowConfirmDeleteContactModal(false);
              setDeletedContact(undefined);
            });
          }
        }}
        loading={updating}
      />

      <EditSupplierCountry
        show={showEditCountryModal}
        onHide={() => setShowEditCountryModal(false)}
        supplierCountry={supplier.country}
        onSave={async (newCountry: string) => {
          const success = await updateSupplier(supplierId, {
            country: newCountry,
          });
          if (success) {
            showToastMessage('Country updated successfully');
            mutate({ country: newCountry });
            setShowEditCountryModal(false);
          } else {
            showToastMessage('Failed to update country');
          }
        }}
        loading={updating}
      />

      <EditSupplierName
        show={showEditNameModal}
        onHide={() => setShowEditNameModal(false)}
        supplierName={supplier.company_name}
        onSave={(val) => {
          updateField('company_name', val);
          setShowEditNameModal(false);
        }}
        loading={false} // set true while saving if you want to disable buttons
      />

      <ToastContainer position={gToastPosition} className="p-3">
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={3000}
          autohide
          bg={gToastSentiment}
        >
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
};

export default SupplierDetails;
